import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import acquisitionClient from "@/services/acquisitionApiClient";
import { Button } from "@/components/ui/button";

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading"); // loading, success, wrong_browser, error

  useEffect(() => {
    const verify = async () => {
      const user_id = searchParams.get("user_id");
      const code = searchParams.get("code");

      if (!user_id || !code) {
        setStatus("error");
        return;
      }

      const redirect = searchParams.get("redirect");
      const signupStartedHere = localStorage.getItem("signup_pending") === "true";

      // Bloqueamos la verificación cuando el enlace se abre en un navegador
      // distinto al que inició el registro: el estado de la sesión de signup
      // vive en localStorage y no puede sincronizarse entre dispositivos.
      if (redirect !== "login" && !signupStartedHere) {
        setStatus("wrong_browser");
        return;
      }

      try {
        const response = await acquisitionClient.post("/customers/verify-account/", {
          user_id,
          code,
        });
        const { token, client_type } = response.data;

        // Guardamos el token en localStorage para que acquisitionClient lo use
        localStorage.setItem("account", JSON.stringify({ user: { token: token } }));
        // También bajo la clave que usa getAcquisitionToken — sin esto, las
        // páginas del flujo particular (ubicacion-precio, amenidades, etc.)
        // rebotan a /unirse/particular porque no encuentran el token.
        localStorage.setItem("acquisitionToken", token);

        setStatus("success");

        // Redirigir al siguiente paso después de 2 segundos
        setTimeout(() => {
          if (redirect === "login") {
            navigate("/login");
            return;
          }
          if (client_type === "property_owner") {
            navigate("/unirse/ubicacion-precio");
          } else {
            // Use a URL param so the handoff is visible in the address bar
            // and survives refresh, instead of relying on a localStorage
            // write or router state that we've seen disappear in production.
            navigate("/unirse/pro?step=1");
          }
        }, 2000);
      } catch (e) {
        setStatus("error");
      }
    };
    verify();
  }, [searchParams, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-md space-y-8 rounded-2xl bg-white p-8 text-center shadow-lg border border-gray-100">
        {status === "loading" && (
          <div className="space-y-4">
            <div className="animate-spin h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
            <h2 className="text-2xl font-bold text-gray-900">Verificando tu cuenta...</h2>
            <p className="text-gray-500">Por favor, espera un momento.</p>
          </div>
        )}

        {status === "success" && (
          <div className="space-y-6">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
              <svg
                className="h-10 w-10 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">¡Cuenta verificada exitosamente!</h2>
              <p className="mt-2 text-gray-600">Te estamos llevando al siguiente paso…</p>
            </div>
          </div>
        )}

        {status === "wrong_browser" && (
          <div className="space-y-6">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-yellow-100">
              <svg
                className="h-10 w-10 text-yellow-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                Abre el enlace en el dispositivo original
              </h2>
              <p className="mt-2 text-gray-600">
                Por seguridad, debes confirmar tu cuenta en el mismo dispositivo y navegador donde
                iniciaste tu registro. Vuelve a ese dispositivo y abre el enlace desde tu correo.
              </p>
            </div>
          </div>
        )}

        {status === "error" && (
          <div className="space-y-6">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
              <svg
                className="h-10 w-10 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Error al verificar</h2>
              <p className="mt-2 text-gray-600">El enlace puede haber expirado o es inválido.</p>
            </div>
            <Button
              variant="outline"
              className="w-full py-6 text-lg rounded-full"
              onClick={() => navigate("/login")}
            >
              Ir a Iniciar Sesión
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
